import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    const justRegistered = sessionStorage.getItem('justRegistered');
    const userName = sessionStorage.getItem('userName');

    if (justRegistered === 'true' && userName) {
      setWelcomeMessage(`Welcome back ${userName}! 👋`);
      sessionStorage.removeItem('justRegistered');
      sessionStorage.removeItem('userName');
      setTimeout(() => {
        setWelcomeMessage('');
      }, 5000);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const isEmail = identifier.includes('@') && identifier.includes('.');
      const credentials = isEmail
        ? { email: identifier, password }
        : { phone: identifier, password };

      const result = await login(credentials);

      if (result.success && result.user) {
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberMe');
        }
        const firstName = result.user.name?.split(' ')[0] || 'User';
        sessionStorage.setItem('userName', firstName);
        navigate('/');
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/Gimbiya.png" alt="Gimbiya" className="logo-image" />
        </div>
        <h2>Welcome Back</h2>
        <p className="auth-subtitle">Shop like a boss with Gimbiya Value Store</p>

        {welcomeMessage && <div className="auth-success">{welcomeMessage}</div>}
        {error && <div className="auth-error">⚠️ {error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <div className="input-wrapper">
              <span className="input-icon">📧</span>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                placeholder="Your email or phone"
                autoComplete="username"
                className="auth-input"
              />
            </div>
          </div>

          <div className="form-group">
            <div className="input-wrapper password-wrapper">
              <span className="input-icon">🔒</span>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Your password"
                autoComplete="current-password"
                className="auth-input password-input"
              />
              <span className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? '🙈' : '👁️'}
              </span>
            </div>
          </div>

          <div className="remember-me">
            <label className="remember-label">
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="forgot-link-inline">Forgot Password?</Link>
          </div>

          {/* In Login.tsx */}
          <div className="button-container" style={{ height: '48px', flexShrink: 0 }}>
            <button 
              type="submit" 
              disabled={loading} 
              className={`login-auth-button ${loading ? 'loading' : ''}`}
            >
              {loading ? 'Signing In...' : 'Login'}
            </button>
          </div>
        </form> 

        <div className="auth-divider">OR</div>

        <div className="social-login">
          <button type="button" className="social-btn"><span>🌍</span><span>Google</span></button>
          <button type="button" className="social-btn"><span>ⓕ</span><span>Facebook</span></button>
        </div>

        <div className="auth-links">
          <p className="auth-link">Don't have an account? <Link to="/register"> Create Account</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Login;